import {
  computed, getCurrentInstance, onMounted, onUnmounted, ref, watch,
} from '@vue/composition-api'
import router from '@/router'
import store from '@/store'
import { processTelemetryData } from '@/utils/telemetryParametersUtils'
import managementStoreModule from '../managementStoreModule'
import assetStoreModule from '../../asset/assetStoreModule'
import { extractHorHod, formatMetric, mapMaintenanceItem } from './maintenanceUtils'

const MANAGEMENT_MODULE = 'app-management'
const ASSET_MODULE = 'app-asset'
const PER_PAGE = 10

export default function useMaintenance() {
  const instance = getCurrentInstance()
  const proxy = instance.proxy || instance
  const assetId = router.currentRoute.params.id

  const asset = ref({})
  const associatedPlans = ref([])
  const allPlansOpt = ref([])
  const rawSummaryItems = ref([])
  const apiHourmeter = ref(null)
  const apiOdometer = ref(null)
  const telemetryHourmeter = ref(null)
  const telemetryOdometer = ref(null)

  const currentPage = ref(1)
  const totalItems = ref(0)
  const selectedPlanId = ref(null)
  const showModal = ref(false)

  const isLoading = ref(true)
  const isLoadingPlans = ref(false)
  const isBinding = ref(false)
  const didRegisterManagement = ref(false)
  const didRegisterAsset = ref(false)

  const registerModules = () => {
    if (!store.hasModule(MANAGEMENT_MODULE)) {
      store.registerModule(MANAGEMENT_MODULE, managementStoreModule)
      didRegisterManagement.value = true
    }
    if (!store.hasModule(ASSET_MODULE)) {
      store.registerModule(ASSET_MODULE, assetStoreModule)
      didRegisterAsset.value = true
    }
  }

  const upcomingMaintenances = computed(() => rawSummaryItems.value
    .map(item => mapMaintenanceItem(
      item,
      telemetryHourmeter.value,
      telemetryOdometer.value,
      apiHourmeter.value,
      apiOdometer.value,
    ))
    .sort((a, b) => a.remaining - b.remaining))

  const nextMaintenance = computed(() => upcomingMaintenances.value[0] || null)

  const hasMaintenancePlan = computed(() => associatedPlans.value.length > 0 || rawSummaryItems.value.length > 0)

  const overdueCount = computed(() => upcomingMaintenances.value.filter(item => item.status === 'overdue').length)

  const currentHourmeter = computed(() => telemetryHourmeter.value ?? apiHourmeter.value)
  const currentOdometer = computed(() => telemetryOdometer.value ?? apiOdometer.value)

  const selectablePlans = computed(() => {
    const boundIds = new Set(associatedPlans.value.map(plan => plan.preventiveMaintenancePlanId))
    return allPlansOpt.value.filter(option => !boundIds.has(option.value))
  })

  const dataMeta = computed(() => {
    const localCount = associatedPlans.value.length
    return {
      from: PER_PAGE * (currentPage.value - 1) + (localCount ? 1 : 0),
      to: PER_PAGE * (currentPage.value - 1) + localCount,
      of: totalItems.value,
    }
  })

  const assetIdentifier = computed(() => asset.value.plate || asset.value.chassisNumber || '')

  const msgDialog = (msg, icon) => {
    proxy.$swal({
      position: 'top-end',
      icon,
      title: msg,
      showConfirmButton: false,
      timer: 4500,
      customClass: { confirmButton: 'btn btn-primary' },
      buttonsStyling: false,
    })
  }

  const fetchAsset = () => store
    .dispatch(`${MANAGEMENT_MODULE}/fetchAsset`, { id: assetId })
    .then(response => {
      asset.value = response.data || {}
    })
    .catch(() => {
      asset.value = {}
    })

  const fetchAssociatedPlans = () => {
    isLoadingPlans.value = true
    return store
      .dispatch(`${MANAGEMENT_MODULE}/fetchPlansByAssetId`, {
        assetId,
        page: currentPage.value,
        perPage: PER_PAGE,
      })
      .then(response => {
        associatedPlans.value = response.data.data || []
        totalItems.value = response.data.totalItems || 0
      })
      .catch(() => {
        associatedPlans.value = []
        totalItems.value = 0
      })
      .finally(() => {
        isLoadingPlans.value = false
      })
  }

  const fetchSummary = () => store
    .dispatch(`${MANAGEMENT_MODULE}/fetchMaintenancePlanSummary`, { id: assetId })
    .then(response => {
      rawSummaryItems.value = response.data.data || []
      apiHourmeter.value = response.data.currentHourmeter
      apiOdometer.value = response.data.currentOdometer
    })
    .catch(() => {
      rawSummaryItems.value = []
    })

  const fetchTelemetry = () => store
    .dispatch(`${ASSET_MODULE}/fetchAssetSummaryParameter`, { assetId })
    .then(response => {
      const telemetryData = processTelemetryData(response?.data?.data || [])
      const { hourmeter, odometer } = extractHorHod(telemetryData)
      telemetryHourmeter.value = hourmeter
      telemetryOdometer.value = odometer
    })
    .catch(() => {
      telemetryHourmeter.value = null
      telemetryOdometer.value = null
    })

  const fetchAllPlans = () => store
    .dispatch(`${MANAGEMENT_MODULE}/fetchAllMaintenancePlan`, {
      q: '',
      perPage: 100,
      page: 1,
    })
    .then(response => {
      allPlansOpt.value = (response.data.data || []).map(plan => ({
        label: plan.name,
        value: plan.id,
      }))
    })
    .catch(() => {
      allPlansOpt.value = []
    })

  const refreshMaintenance = () => Promise.all([fetchAssociatedPlans(), fetchSummary()])

  const openModalAddPlan = () => {
    selectedPlanId.value = null
    showModal.value = true
  }

  const closeModal = () => {
    selectedPlanId.value = null
    showModal.value = false
  }

  const bindPlan = () => {
    if (!selectedPlanId.value || isBinding.value) return

    isBinding.value = true
    store.dispatch(`${MANAGEMENT_MODULE}/bindMaintenancePlan`, {
      assetId,
      preventiveMaintenancePlanId: selectedPlanId.value,
    })
      .then(() => {
        closeModal()
        msgDialog(proxy.$t('Asset added to maintenance plan'), 'success')
        return refreshMaintenance()
      })
      .catch(() => {
        msgDialog(proxy.$t('An error occurred'), 'error')
      })
      .finally(() => {
        isBinding.value = false
      })
  }

  const deleteBindPlan = (bindId, planId) => {
    store.dispatch(`${MANAGEMENT_MODULE}/deleteBindPlan`, {
      assetId,
      preventiveMaintenancePlanId: planId,
      assetMaintenancePlanId: bindId,
    })
      .then(() => {
        proxy.$swal(proxy.$t('Deleted'), proxy.$t('You successfully deleted'), 'success')
        return refreshMaintenance()
      })
      .catch(() => {
        proxy.$swal(proxy.$t('Error'), proxy.$t('An error occurred while trying to delete'), 'error')
      })
  }

  const confirmRemovePlan = (bindId, planId) => {
    proxy.$swal({
      title: proxy.$t('Are you sure?'),
      text: proxy.$t("You can't revert your action"),
      type: 'warning',
      showCancelButton: true,
      confirmButtonText: proxy.$t('Yes'),
      cancelButtonText: proxy.$t('No'),
      showCloseButton: true,
      showLoaderOnConfirm: true,
      customClass: {
        cancelButton: 'cancelBtn',
        confirmButton: 'mr-1',
      },
    }).then(result => {
      if (result.value) deleteBindPlan(bindId, planId)
    })
  }

  const setSelectedPlanId = (id) => {
    selectedPlanId.value = id
  }

  const setCurrentPage = (page) => {
    currentPage.value = page
  }

  watch(currentPage, () => {
    fetchAssociatedPlans()
  })

  registerModules()

  onMounted(async () => {
    isLoading.value = true
    await Promise.all([
      fetchAsset(),
      fetchAssociatedPlans(),
      fetchSummary(),
      fetchTelemetry(),
      fetchAllPlans(),
    ])
    isLoading.value = false
  })

  onUnmounted(() => {
    if (didRegisterManagement.value && store.hasModule(MANAGEMENT_MODULE)) {
      store.unregisterModule(MANAGEMENT_MODULE)
    }
    if (didRegisterAsset.value && store.hasModule(ASSET_MODULE)) {
      store.unregisterModule(ASSET_MODULE)
    }
  })

  return {
    asset,
    assetId,
    assetIdentifier,
    associatedPlans,
    selectablePlans,
    upcomingMaintenances,
    nextMaintenance,
    hasMaintenancePlan,
    overdueCount,
    currentHourmeter,
    currentOdometer,
    currentPage,
    totalItems,
    perPage: PER_PAGE,
    dataMeta,
    selectedPlanId,
    showModal,
    isLoading,
    isLoadingPlans,
    isBinding,
    formatMetric,
    openModalAddPlan,
    closeModal,
    bindPlan,
    confirmRemovePlan,
    setSelectedPlanId,
    setCurrentPage,
  }
}
